import type { ConsigneeValue } from "@RequestFormV2/defaults/consigneeDefaults";
import type { NotifyPartyValue } from "@RequestFormV2/defaults/notifyPartyDefaults";
import type { ScheduleStopValue } from "@RequestFormV2/defaults/scheduleDefaults";
import { partyContactKey, resolvePartySyncKind, type PartySyncInput, type PartySyncKind } from "helpers/partySyncKind";
import { StopAddressType, StopType } from "types/MultiStop";

export type PartyReviewSyncKind = PartySyncKind;

const normalizedKeyPart = (value?: string | null): string => (value ?? "").trim();

type PartyLocationFields = {
  address_type?: string;
  address_reference_number?: string;
  airport?: string;
  seaport?: string;
};

export const getPartyLocationKey = (party: PartyLocationFields): string => {
  const addressType = String(party.address_type ?? "").toLowerCase();
  if (addressType === "airport") {
    const code = normalizedKeyPart(party.airport);
    return code ? `airport:${code}` : "";
  }
  if (addressType === "seaport" || addressType === "port") {
    const code = normalizedKeyPart(party.seaport);
    return code ? `seaport:${code}` : "";
  }
  const reference = normalizedKeyPart(party.address_reference_number);
  return reference ? `address:${reference}` : "";
};

export const getDeliveryLocationKey = (stop?: ScheduleStopValue | null): string => {
  if (!stop) return "";
  if (stop.stop_address_type === StopAddressType.AIRPORT) {
    const code = normalizedKeyPart(stop.stop_airport_code);
    return code ? `airport:${code}` : "";
  }
  if (stop.stop_address_type === StopAddressType.SEAPORT) {
    const code = normalizedKeyPart(stop.stop_seaport_code);
    return code ? `seaport:${code}` : "";
  }
  const reference = normalizedKeyPart(stop.address_reference_number);
  return reference ? `address:${reference}` : "";
};

export const getFinalDeliveryStop = (stops?: ScheduleStopValue[] | null): ScheduleStopValue | undefined => {
  if (!stops?.length) return undefined;
  for (let index = stops.length - 1; index >= 0; index -= 1) {
    if (stops[index].stop_type === StopType.DROPOFF) return stops[index];
  }
  return undefined;
};

const toReviewPartyInput = (party: ConsigneeValue | NotifyPartyValue): PartySyncInput => {
  const formType = party.form_address_contact_type;
  const sameAsOption = "same_as_address_contact" in party ? party.same_as_address_contact : undefined;
  return {
    locationKey: getPartyLocationKey(party),
    contactKey: partyContactKey(party.contact_name, party.contact_phone, party.contact_email),
    sameAsDelivery:
      ("same_as_delivery_address_contact" in party && party.same_as_delivery_address_contact) ||
      formType === "delivery" ||
      sameAsOption === "sameAsDeliveryAddressContact",
    sameAsConsignee: formType === "consignee" || sameAsOption === "sameAsConsigneeAddressContact",
    allowLocationInference: !formType && !sameAsOption,
  };
};

const toReviewDeliveryInput = (stop?: ScheduleStopValue): PartySyncInput => ({
  locationKey: getDeliveryLocationKey(stop),
  contactKey: partyContactKey(stop?.stop_contact_name, stop?.stop_contact_number, stop?.stop_contact_email),
});

export const resolveConsigneeReviewSyncKind = (
  consignee: ConsigneeValue,
  deliveryStop?: ScheduleStopValue,
): PartyReviewSyncKind | null => resolvePartySyncKind(toReviewPartyInput(consignee), toReviewDeliveryInput(deliveryStop));

export const resolveNotifyReviewSyncKind = (
  notifyParty: NotifyPartyValue,
  consignee?: ConsigneeValue,
  deliveryStop?: ScheduleStopValue,
): PartyReviewSyncKind | null =>
  resolvePartySyncKind(toReviewPartyInput(notifyParty), toReviewDeliveryInput(deliveryStop), {
    role: "notify",
    ...(consignee ? { consignee: toReviewPartyInput(consignee) } : {}),
  });
